
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import lodash from "lodash";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import {
  AlertBoxOptions,
  OEmbed,
  OEmbedV,
  VideoOptions
} from "@/types/controls";
import { Media } from "@/types/media";
import Icon from "@/components/UI/Icon.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import MediaUploadDialog from "@/components/Reusable/CustomInputs/MediaInput/MediaUploadDialog.vue";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";

@Component({
  components: { Icon, MediaUploadDialog, TextInput, AlertBox }
})
export default class VideoInput extends Vue {
  @Prop()
  value?: OEmbed;

  @Prop()
  options?: VideoOptions;

  @Prop()
  autofocus?: boolean;

  debouncedOnInput = lodash.debounce(this.onInput, 300);

  loading = false;
  error = false;
  dialogOpen = false;

  get state() {
    if (this.error) return "error";
    if (this.loading) return "loading";
    if (this.value) return "loaded";
    return "empty";
  }

  async getEmbed(url: string) {
    return pipe(
      await makeWordPressAjaxRequest({
        friendlyName: "Get Video Embed",
        action: "breakdance_oembed",
        dataToPost: { url },
        decoder: OEmbedV
      }),
      fold(
        () => undefined,
        expectedDataToLoad => expectedDataToLoad
      )
    );
  }

  get allowedProviders() {
    return this.options?.providers ?? [];
  }

  isOEmbedValid(oembed?: OEmbed) {
    if (!oembed) return false;
    if (!this.allowedProviders?.length) return true;
    if (!oembed.provider) return true; // TODO: Provider could be empty. Why?

    return this.allowedProviders.includes(oembed.provider);
  }

  oembedErrorMessage = "There was an error embedding the video";
  async setOEmbed(url: string) {
    this.oembedErrorMessage = "There was an error embedding the video";
    this.loading = true;
    const oembed = await this.getEmbed(url);
    this.loading = false;

    if (!oembed) {
      this.oembedErrorMessage = "Unable to retrieve a video from this URL";
      this.error = true;
      return;
    }

    if (!this.isOEmbedValid(oembed)) {
      this.oembedErrorMessage = `Videos from ${
        oembed.provider ?? "this platform"
      } are not allowed for this field. Allowed embed platforms are ${this.allowedProviders.join(
        ", "
      )}`;
      this.error = true;
      return;
    }

    this.edit({ ...oembed, type: "oembed", source: oembed.provider });
  }

  onInput(url: string | null) {
    this.error = false;
    this.loading = false;

    if (!url) {
      this.clear();
    } else if (this.isFileURL(url)) {
      this.setFileURL(url, this.getMimeTypeFromUrl(url));
    } else {
      this.setOEmbed(url);
    }
  }

  getMimeTypeFromUrl(url: string) {
    const extensionOverrides: Record<string, string> = {
      ogv: "video/ogg",
      flv: "video/x-flv"
    };

    const fileExtension = url.split(".").pop() || "";
    return extensionOverrides[fileExtension] ?? `video/${fileExtension}`;
  }

  // TODO: Extracted directly from WP codebase. Does this function cover all cases?
  isFileURL(url: string) {
    const extensions = ["mp4", "m4v", "webm", "ogv", "flv"];
    const extension = url.split(".").pop() || "";
    return extensions.includes(extension);
  }

  setFileURL(url: string, mime?: string) {
    this.edit({
      url,
      mime,
      embedUrl: url,
      format: "video",
      type: "video",
      source: "local"
    });
  }

  // Upload Dialog
  onMediaSelected(media: Media) {
    if (media.type !== "video") {
      this.error = true;
      return;
    }

    this.setFileURL(media.url, media.mime);
  }

  edit(newVal?: OEmbed) {
    this.$emit("input", newVal);
  }

  clear() {
    this.edit(undefined);
  }

  openDialog() {
    this.dialogOpen = true;
  }

  closeDialog() {
    this.dialogOpen = false;
  }

  get oembedErrorOptions(): AlertBoxOptions | false {
    return {
      style: "error",
      content: this.oembedErrorMessage
    };
  }

  get allowedProviderString() {
    if (this.allowedProviders.length > 0) {
      return `from ${this.allowedProviders.join(", ")}`;
    }
    return "";
  }
}
